import { t } from "@lingui/macro";
import { CircleNotch, FilePdf } from "@phosphor-icons/react";
import type { ResumeDto } from "@reactive-resume/dto";
import { Button } from "@reactive-resume/ui";
import { pageSizeMap } from "@reactive-resume/utils";
import { useCallback, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import type { LoaderFunction } from "react-router";
import { Link, redirect, useLoaderData } from "react-router";

import { Icon } from "@/client/components/icon";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { queryClient } from "@/client/libs/query-client";
import { DOWNLOADS_COUNT_TO_SHOW_FEEDBACK, openFeedbackModal } from "@/client/libs/tally";
import { findResumeByUsernameSlug, usePrintResume } from "@/client/services/resume";
import { useUser } from "@/client/services/user";
import { isLoggedIn } from "@/client/common/helpers";

const openInNewTab = (url: string) => {
  const win = window.open(url, "_blank");
  if (win) win.focus();
};

export const PublicResumePage = () => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const { refetch } = useUser(false);

  const { printResume, loading } = usePrintResume();

  const { id, title, data: resume } = useLoaderData();
  const format = resume.metadata.page.format as keyof typeof pageSizeMap;

  const updateResumeInFrame = useCallback(() => {
    const message = { type: "SET_RESUME", payload: resume };

    setImmediate(() => {
      frameRef.current?.contentWindow?.postMessage(message, "*");
    });
  }, [resume]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    frame.addEventListener("load", updateResumeInFrame);

    return () => {
      frame.removeEventListener("load", updateResumeInFrame);
    };
  }, [updateResumeInFrame]);

  useEffect(() => {
    updateResumeInFrame();
  }, [updateResumeInFrame]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame?.contentWindow) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data.type !== "PAGE_LOADED" || !frameRef.current) return;

      frameRef.current.width = event.data.payload.width;
      frameRef.current.height = event.data.payload.height;
    };

    frame.contentWindow.addEventListener("message", handleMessage);

    return () => {
      frame.contentWindow?.removeEventListener("message", handleMessage);
    };
  }, []);

  const onDownloadPdf = async () => {
    const { url } = await printResume({ id });

    openInNewTab(url);
    if (isLoggedIn()) {
      const { data: user } = await refetch();
      if (user?.totalResumeDownloads === DOWNLOADS_COUNT_TO_SHOW_FEEDBACK) {
        openFeedbackModal({ layout: 'modal', width: 500, autoClose: 3000, hideTitle: true });
      }
    }
  };

  return (
    <div>
      <Helmet>
        <title>
          {title} - {t`Resumotion`}
        </title>
      </Helmet>

      <div
        style={{ width: `${pageSizeMap[format].width}mm` }}
        className="relative z-50 overflow-hidden rounded shadow-xl sm:mx-auto sm:mb-6 sm:mt-16 print:m-0 print:shadow-none"
      >
        <iframe
          ref={frameRef}
          title={title}
          src="/artboard/preview"
          style={{ width: `${pageSizeMap[format].width}mm`, overflow: "hidden" }}
        />
      </div>

      <div className="hidden justify-center py-10 opacity-50 sm:flex print:hidden">
        <Link to="/">
          <Button size="sm" variant="ghost" className="space-x-1.5 text-xs font-normal">
            <span>{t`Built with`}</span>
            <Icon size={12} />
            <span>{t`Resumotion`}</span>
          </Button>
        </Link>
      </div>

      <div className="fixed bottom-5 right-5 z-0 hidden sm:block print:hidden">
        <div className="flex flex-col items-center gap-y-2">
          <Button size="icon" variant="ghost" onClick={onDownloadPdf}>
            {loading ? <CircleNotch size={20} className="animate-spin" /> : <FilePdf size={20} />}
          </Button>

          <ThemeSwitch />
        </div>
      </div>
    </div>
  );
};

export const publicLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const username = params.username!;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const slug = params.slug!;

    return await queryClient.fetchQuery({
      queryKey: ["resume", { username, slug }],
      queryFn: () => findResumeByUsernameSlug({ username, slug }),
    });
  } catch {
    return redirect("/");
  }
};
